import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { WINE_LETTERS, type MergePreview } from '@gwc/shared';
import { historyApi, type ContributionDetail, type MergeRequest } from '../api/historyContributions';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { Badge, Button, Dialog } from './ui';
import { PersonPicker } from './PersonPicker';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The reviewer's last step. Confirms the few things the database requires
 * (title, year, a letter per wine), shows exactly what will be created or
 * filled — from a server-side dry run — and merges.
 */
export function ContributionMergeDialog({
  detail,
  open,
  onClose,
  onMerged,
}: {
  detail: ContributionDetail;
  open: boolean;
  onClose: () => void;
  onMerged: (eventId: string) => void;
}) {
  const { contribution, linkedEvent } = detail;
  const ev = contribution.payload.event ?? {};
  const [title, setTitle] = useState(ev.title ?? linkedEvent?.title ?? '');
  const [year, setYear] = useState<string>(String(ev.year ?? linkedEvent?.eventYear ?? ''));
  const [month, setMonth] = useState<string>(String(ev.month ?? linkedEvent?.eventMonth ?? ''));
  const [day, setDay] = useState<string>(String(ev.day ?? (linkedEvent?.eventDate ? Number(linkedEvent.eventDate.slice(8, 10)) : '')));
  const [useLinkedEvent, setUseLinkedEvent] = useState(!!linkedEvent);
  const [letters, setLetters] = useState<Record<string, string>>({});
  const [personMap, setPersonMap] = useState<Record<string, string>>({});
  const [createUnknownPeople, setCreateUnknownPeople] = useState(true);
  const [overwriteScores, setOverwriteScores] = useState(false);
  const [overwriteEventFields, setOverwriteEventFields] = useState(false);
  const [preview, setPreview] = useState<MergePreview | null>(null);

  const body = useMemo<MergeRequest | null>(() => {
    if (!title.trim() || !year) return null;
    return {
      title: title.trim(),
      eventYear: Number(year),
      eventMonth: month ? Number(month) : null,
      eventDay: day ? Number(day) : null,
      useLinkedEvent,
      letters,
      personMap,
      createUnknownPeople,
      overwriteScores,
      overwriteEventFields,
    };
  }, [title, year, month, day, useLinkedEvent, letters, personMap, createUnknownPeople, overwriteScores, overwriteEventFields]);

  const previewMut = useMutation({
    mutationFn: (b: MergeRequest) => historyApi.mergePreview(contribution.id, b),
    onSuccess: setPreview,
  });
  useEffect(() => {
    if (!open || !body) return;
    const t = setTimeout(() => previewMut.mutate(body), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, body]);

  const merge = useMutation({
    mutationFn: (b: MergeRequest) => historyApi.merge(contribution.id, b),
    onSuccess: (r) => {
      toast.success('Merged into the archive');
      onMerged(r.summary.eventId);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Merge failed'),
  });

  const usedLetters = new Set([
    ...(contribution.payload.wines ?? []).map((w) => w.letter).filter(Boolean),
    ...Object.values(letters),
  ]);
  const blockers = preview ? preview.wines.filter((w) => w.outcome === 'needs_letter' || w.outcome === 'conflict') : [];
  const previewError = previewMut.error instanceof ApiError ? previewMut.error.message : previewMut.error ? 'Preview failed' : null;
  const canMerge = !!body && !!preview && blockers.length === 0 && !previewMut.isPending && !previewError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Merge into the archive"
      description="Confirm what the records need, check the preview, then merge. Existing details are only filled in, never overwritten, unless you say so."
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-cream-300/60">{previewMut.isPending ? 'Previewing…' : preview ? 'Preview is live — it updates as you change things.' : ''}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => body && merge.mutate(body)} disabled={!canMerge} loading={merge.isPending}>
              Merge into archive
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 text-sm">
        {linkedEvent && (
          <label className="flex items-center gap-2 rounded border border-gold-600/30 bg-gold-500/5 px-3 py-2">
            <input type="checkbox" checked={useLinkedEvent} onChange={(e) => setUseLinkedEvent(e.target.checked)} />
            <span>
              Add to the existing tasting <strong className="text-gold-200">{linkedEvent.title}</strong> ({linkedEvent.eventYear})
            </span>
          </label>
        )}
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_1fr]">
          <label className="block text-xs text-cream-300/70">
            <span className="mb-1 block">Title *</span>
            <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} disabled={useLinkedEvent && !!linkedEvent} />
          </label>
          <label className="block text-xs text-cream-300/70">
            <span className="mb-1 block">Year *</span>
            <input className="field" type="number" min={1900} max={2100} value={year} onChange={(e) => setYear(e.target.value)} />
          </label>
          <label className="block text-xs text-cream-300/70">
            <span className="mb-1 block">Month</span>
            <select className="field" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">unknown</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-cream-300/70">
            <span className="mb-1 block">Day</span>
            <input className="field" type="number" min={1} max={31} value={day} disabled={!month} onChange={(e) => setDay(e.target.value)} />
          </label>
        </div>

        {previewError && <p className="rounded border border-wine-700/50 bg-wine-900/20 px-3 py-2 text-wine-300">{previewError}</p>}

        {preview && (
          <>
            <div>
              <h3 className="mb-2 text-xs uppercase tracking-[0.15em] text-gold-400">Wines</h3>
              {preview.wines.length === 0 ? (
                <p className="text-cream-300/60">No wines described.</p>
              ) : (
                <ul className="space-y-1">
                  {preview.wines.map((w) => (
                    <li key={w.key} className="flex flex-wrap items-center gap-2">
                      {w.outcome === 'needs_letter' ? (
                        <select
                          className="field w-20 py-1"
                          value={letters[w.key] ?? ''}
                          onChange={(e) => setLetters((m) => ({ ...m, [w.key]: e.target.value }))}
                        >
                          <option value="">letter?</option>
                          {WINE_LETTERS.filter((l) => !usedLetters.has(l) || letters[w.key] === l).map((l) => (
                            <option key={l} value={l}>
                              {l}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="w-20 font-display text-gold-200">{w.letter}</span>
                      )}
                      <span className="text-cream-100">{w.label}</span>
                      <Badge tone={w.outcome === 'create' ? 'success' : w.outcome === 'fill' ? 'info' : w.outcome === 'conflict' ? 'danger' : 'warning'}>
                        {w.outcome === 'create' ? 'new slot' : w.outcome === 'fill' ? 'fills existing slot' : w.outcome === 'conflict' ? 'conflict' : 'needs a letter'}
                      </Badge>
                      {w.detail && <span className="text-xs text-wine-300">{w.detail}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {preview.wines.some((w) => w.outcome === 'conflict') && (
                <label className="mt-2 flex items-center gap-2 text-xs text-cream-300/70">
                  <input type="checkbox" checked={overwriteEventFields} onChange={(e) => setOverwriteEventFields(e.target.checked)} />
                  Overwrite existing tasting and wine details with this contribution's
                </label>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-xs uppercase tracking-[0.15em] text-gold-400">People</h3>
              {preview.people.length === 0 ? (
                <p className="text-cream-300/60">No people named.</p>
              ) : (
                <ul className="space-y-2">
                  {preview.people.map((p) => (
                    <li key={p.key} className="grid gap-2 sm:grid-cols-[1fr_auto_1.2fr] sm:items-center">
                      <span className="text-cream-100">{p.name}</span>
                      <Badge tone={p.outcome === 'matched' || p.outcome === 'picked' ? 'success' : p.outcome === 'will_create' ? 'warning' : 'danger'}>
                        {p.outcome === 'matched' ? 'matched' : p.outcome === 'picked' ? 'chosen' : p.outcome === 'will_create' ? 'will be added to the records' : 'unresolved'}
                      </Badge>
                      {p.outcome !== 'matched' && (
                        <PersonPicker
                          placeholder="…or pick the right person"
                          className="field py-1"
                          value={personMap[p.key] ? { personId: personMap[p.key], name: p.name } : null}
                          onChange={(v) =>
                            setPersonMap((m) => {
                              const next = { ...m };
                              if (v?.personId) next[p.key] = v.personId;
                              else delete next[p.key];
                              return next;
                            })
                          }
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <label className="mt-2 flex items-center gap-2 text-xs text-cream-300/70">
                <input type="checkbox" checked={createUnknownPeople} onChange={(e) => setCreateUnknownPeople(e.target.checked)} />
                Add unmatched names to the records as non-members (otherwise their scores are skipped)
              </label>
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              {[
                ['Tasting', preview.eventCreated ? 'created' : 'existing'],
                ['Wines', `${preview.winesCreated} new · ${preview.winesUpdated} filled`],
                ['Scores', `${preview.scoresCreated} new · ${preview.scoresUpdated} replaced · ${preview.scoresSkipped.length} skipped`],
                ['Attendance', String(preview.attendeesRecorded)],
                ['Photos & scans', `${preview.mediaCreated} → gallery`],
                ['Spreadsheets', `${preview.importBatchesCreated} → Imports`],
              ].map(([k, v]) => (
                <div key={k} className="rounded border border-ink-800 bg-ink-900/40 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-cream-300/50">{k}</p>
                  <p className="text-cream-100">{v}</p>
                </div>
              ))}
            </div>
            {preview.scoresSkipped.length > 0 && (
              <details className="text-xs text-cream-300/70">
                <summary className="cursor-pointer">Skipped scores ({preview.scoresSkipped.length})</summary>
                <ul className="mt-1 list-disc pl-5">
                  {preview.scoresSkipped.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </details>
            )}
            {preview.scoresSkipped.some((s) => s.includes('already has a score')) && (
              <label className="flex items-center gap-2 text-xs text-cream-300/70">
                <input type="checkbox" checked={overwriteScores} onChange={(e) => setOverwriteScores(e.target.checked)} />
                Replace existing scores with the ones in this contribution
              </label>
            )}
            {preview.warnings.length > 0 && (
              <ul className="list-disc pl-5 text-xs text-gold-200">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
